import React from 'react'
import classNames from 'classnames'
import { Card } from 'transfix-ui/components/Card'
import { makeStyles, Typography } from 'transfix-ui/core'
import { brandColors } from 'transfix-ui/theme/colors'
import { IAdditionalStops } from 'apps/marketplace/store/amQuoting/types'
import { ICostControlConfig } from 'apps/spot-quote-control-panel/types/costControlConfig'
import { formatRate } from 'utils/formatRate'
import { CostCalTableRow } from './CostCalTableRow'

interface ITcmCardProps {
  allIn?: number | null
  tcm25?: number | null
  tcm40?: number | null
  tcm50?: number | null
  tcm75?: number | null
  additionalStops?: IAdditionalStops[]
  className?: string
  appliedCostControlConfig?: ICostControlConfig | null
  adjustedCostBasis?: number | null
}

const enum TOOLTIP_VALS {
  tcm25 = 'For a shipment with these characteristics, the Transfix Cost Model predicts that we can cover below this cost 25% of the time if we covered this shipment today.',
  tcm75 = 'For a shipment with these characteristics, the Transfix Cost Model predicts that we can cover below this cost 75% of the time if we covered this shipment today.',
  tcm = 'For a shipment with these characteristics, the Transfix Cost Model predicts that we can cover below this cost 50% of the time if we covered this shipment today.',
  adjustedCostBasis = 'This is only available on Margin-Optimized Quotes, and reflects the cost basis after adjustments are applied by the Strike Price Control Panel.',
}

const useStyles = makeStyles(() => ({
  cardContainer: {
    height: '100%',
  },
  fontColor: {
    color: brandColors.coolGray5,
  },
}))

export const TcmCard = ({
  additionalStops,
  adjustedCostBasis,
  allIn,
  appliedCostControlConfig,
  className,
  tcm25,
  tcm40,
  tcm50,
  tcm75,
}: ITcmCardProps) => {
  const classes = useStyles()

  const cardClass = classNames(className || classes.cardContainer)
  if ((!tcm40 && !tcm50 && !tcm25 && !tcm75) || !appliedCostControlConfig?.costStrategy || !allIn) {
    return (
      <Card className={cardClass}>
        <Typography variant='overline'>TCM</Typography>
        <div>TCM data is not available for this price request</div>
      </Card>
    )
  }
  const getCostStrategyTCM = () =>
    appliedCostControlConfig?.costStrategy === 'TCM_50' ? 'TCM 50' : 'TCM 40'

  const getCostStrategyTCMValue = () =>
    appliedCostControlConfig?.costStrategy === 'TCM_50' ? tcm50 : tcm40

  const formatRatioPercentage = (
    numerator: number,
    denominator?: number | null,
    numDecimals: number = 2
  ) => {
    if (!denominator) {
      return 'N/A'
    }
    const ratio = ((numerator / denominator) * 100).toFixed(numDecimals)
    return `${ratio}%`
  }
  const isAdditionalStop = !!additionalStops

  return (
    <Card className={cardClass}>
      <Typography variant='overline'>TCM</Typography>
      <table className='table'>
        <tbody>
          <CostCalTableRow
            label={getCostStrategyTCM()}
            value={formatRate(getCostStrategyTCMValue())}
            stops={isAdditionalStop}
            tooltip={TOOLTIP_VALS.tcm}
            labelClass={classes.fontColor}
          />
          <CostCalTableRow
            label={`All-In to ${getCostStrategyTCM()} %`}
            value={formatRatioPercentage(allIn, getCostStrategyTCMValue())}
            stops={isAdditionalStop}
            labelClass={classes.fontColor}
          />
          <CostCalTableRow
            label='TCM 25'
            value={formatRate(tcm25)}
            stops={isAdditionalStop}
            tooltip={TOOLTIP_VALS.tcm25}
            labelClass={classes.fontColor}
          />
          <CostCalTableRow
            label='TCM 75'
            value={formatRate(tcm75)}
            stops={isAdditionalStop}
            tooltip={TOOLTIP_VALS.tcm75}
            labelClass={classes.fontColor}
          />
          {adjustedCostBasis && (
            <CostCalTableRow
              label='Adjusted Cost Basis'
              value={formatRate(adjustedCostBasis)}
              stops={isAdditionalStop}
              tooltip={TOOLTIP_VALS.adjustedCostBasis}
              labelClass={classes.fontColor}
            />
          )}
        </tbody>
      </table>
    </Card>
  )
}
